// ============================================================
// The Multiple Hits table (plan body-map, Track R2) - where the later hits of
// a burst land on a humanoid body.
//
// The first hit lands where the attack roll says (reversed digits); each later
// hit follows it by the table, so a burst walks across the body instead of
// scattering at random:
//
//   first  | hits 2-6
//   head   | head, arm, body, arm, body
//   arm    | arm, body, head, body, arm
//   body   | body, arm, head, arm, body
//   leg    | leg, body, arm, head, body
//   past six: the body
//
// An "arm" or "leg" is the same side as the first hit's limb, then the other,
// in turn (one side shared by arms and legs; a first hit off the limbs starts
// on the right). The table is as the user remembers the book (ruling
// 2026-10-09), to be corrected if the book differs.
//
// Bodies read by another die (vehicles, the amorphous mass) keep rolling each
// hit. Pure, except that fallback roll. Zero React dependency.
// ============================================================

import { getRandomHitLocationForBody, type BodyConfig } from './bodyConfig';
import type { HitLocation } from '../types';
type Part = 'head' | 'arm' | 'body' | 'leg';

const TABLE: Record<Part, readonly Part[]> = {
  head: ['head', 'arm', 'body', 'arm', 'body'],
  arm: ['arm', 'body', 'head', 'body', 'arm'],
  body: ['body', 'arm', 'head', 'arm', 'body'],
  leg: ['leg', 'body', 'arm', 'head', 'body'],
};

const PART: Record<string, Part> = {
  head: 'head',
  body: 'body',
  rightArm: 'arm',
  leftArm: 'arm',
  rightLeg: 'leg',
  leftLeg: 'leg',
};
const LIMB: Partial<Record<Part, string>> = { arm: 'Arm', leg: 'Leg' };

/** Hit `k`'s id (k >= 1) after a first hit at `firstId`, by the table: a limb flips side once per limb before it. */
function tableId(firstId: string, part: Part, k: number): string {
  const row = TABLE[part];
  const next = row[k - 1] ?? 'body';
  const limb = LIMB[next];
  if (!limb) return next;
  const flips = row.slice(0, k - 1).filter((p) => LIMB[p]).length;
  const startLeft = firstId.startsWith('left');
  return (startLeft !== (flips % 2 === 1) ? 'left' : 'right') + limb;
}

/**
 * Hit `i`'s location (0 = the first) in a burst whose first hit landed at `first`. A body without the table (no
 * reversed digits, or a first hit the table does not know) rolls each later hit instead.
 */
export function burstLocation(i: number, first: HitLocation, config: BodyConfig): HitLocation {
  if (i === 0) return first;
  const part = PART[first.id];
  if (!config.usesDigitReversal || !part) return getRandomHitLocationForBody(config).loc;
  const id = tableId(first.id, part, i);
  return config.locations.find((l) => l.id === id) ?? first;
}

/** A forecast's `locationAt`: the first hit drawn once (a random location unless given), every later one by the table. */
export function burstLocations(
  config: BodyConfig,
  pickFirst: () => HitLocation = () => getRandomHitLocationForBody(config).loc,
): (i: number) => HitLocation {
  let first: HitLocation | null = null;
  return (i) => {
    if (i === 0 || !first) first = pickFirst();
    return burstLocation(i, first, config);
  };
}
